import React from 'react';

import { showModal } from 'modal.react.js';

// eslint-disable-next-line import/named,import/no-duplicates
import { AxiosResponse } from 'axios';
import area from 'components/shared/GlobalModalArea/area';
import AlertModal from 'components/shared/GlobalModalArea/Modal/AlertModal';
import { PENDING_FILE_TASKS } from 'constants/index';
import { showSnackbar } from 'hooks/useSnackbar';
import i18n from 'i18n';

import { confirmAlert } from '../alert';
import axiosApi from '../axios';
import { saveFile } from '../common';
import { LAST_SUCCESSFUL_TASK_ID, SNACKBAR_ID } from './constants';
import { TextCarousel } from './textCarousel';
import { ITask, ITaskConfig } from './types';
// eslint-disable-next-line import/no-duplicates

const showDocxDownloadingConfirmation = (fileUrl: AxiosResponse<string, any>, config: ITaskConfig) =>
  showModal(area, (close) => {
    return (
      <AlertModal
        headerText="Download Word Doc"
        bodyText={
          <div>
            <p style={{ fontSize: '20px', fontWeight: 420 }}>{`The file "${config.filename}" is ready`}</p>
            {config.hint}
          </div>
        }
        confirmButtonText="Download"
        onAccept={async () => {
          close();
          await handleSaveFile(fileUrl, config);
        }}
        res={close}
        footerClassName="buttons-wider"
      />
    );
  });

const handleSaveFile = async (fileUrl: AxiosResponse<string, any>, config: ITaskConfig) => {
  const response = await fetch(fileUrl.data);
  const file = await response.blob();

  saveFile(file as File, config.filename);
};

export async function getFileByTask(config: ITaskConfig, task: ITask) {
  config = getTaskConfig(config);

  const { taskId, title } = task;

  if (!isTaskInQueue(taskId)) {
    pushTaskInQueue(task);
  } else if (isTaskExpired(taskId)) {
    return removeTask(taskId);
  }

  const closeSnackbar = showSnackbar({
    text: <TextCarousel id={taskId} title={title} statuses={config.statuses} translate={config.translate} />,
    prohibitManualClose: true,
    autoClose: false,
    uniqueId: SNACKBAR_ID + taskId,
  });

  try {
    const forever = true;

    while (forever) {
      const status = await axiosApi.get(config.pingTaskURL as string).then(({ data }) => data);
      if (status === 2) {
        throw new Error('server side file error');
      }
      if (status === 3 || !window.navigator.onLine) {
        break;
      }

      await new Promise((res) => setTimeout(res, 5000));
    }

    const fileUrl = await axiosApi.get<string>(config.downloadURL as string);

    window.localStorage.setItem(LAST_SUCCESSFUL_TASK_ID, taskId);

    if (task.extension === 'docx') {
      await showDocxDownloadingConfirmation(fileUrl, config);
    } else {
      const { translate, filename, hint } = config;
      confirmAlert({
        title: translate
          ? i18n.t('proposal_content.file.file_ready', { proposal_title: filename })
          : `The file "${filename}" is ready`,
        hintBottom: hint,
        confirmButtonText: translate ? i18n.t('proposal_content.file.actions.save') : 'Save to my device',
        cancelButtonText: translate ? i18n.t('common.buttons.cancel') : 'Cancel',
        onConfirm: async () => {
          await handleSaveFile(fileUrl, config);
        },
      });
    }
  } finally {
    closeSnackbar && closeSnackbar();

    removeTask(taskId);
  }
}

export function getPendingTasks(): ITask[] {
  return JSON.parse(window.sessionStorage.getItem(PENDING_FILE_TASKS) as string) ?? [];
}

function updatePendingTasks(tasks: ITask[]) {
  const validTasks = tasks.filter((t) => t.taskId && t.itemId);

  sessionStorage.setItem(PENDING_FILE_TASKS, JSON.stringify(validTasks));
}

function pushTaskInQueue(task: ITask) {
  const tasks = getPendingTasks();

  tasks.push(task);

  updatePendingTasks(tasks);
}

function removeTask(taskId: string) {
  const tasks = getPendingTasks();

  const index = tasks.findIndex((s) => s.taskId === taskId);

  if (index > -1) {
    tasks.splice(index, 1);

    updatePendingTasks(tasks);
  }
}

export function isDownloadingInProgress(uniqId: string) {
  return getPendingTasks()
    .map((s) => s.itemId)
    .includes(uniqId);
}

function isTaskInQueue(taskId: string) {
  return !!getTask(taskId);
}

function getTask(taskId: string) {
  const tasks = getPendingTasks();

  return tasks.find((s) => s.taskId === taskId);
}

function isTaskExpired(taskId: string) {
  const task = getTask(taskId);

  if (!task) return true;

  return Date.now() - task.time > 1000 * 60 * 5;
}

export function getTaskConfig(customConfig: ITaskConfig) {
  const defaultConfig = {
    initialPayload: {},
    createTaskURL: '/file',
    pingTaskURL: (taskId: string) => `/file/${taskId}`,
    downloadURL: (taskId: string) => `/file/${taskId}/download`,
    uniqId: undefined,
    statuses: ['Please, wait...'],
    filename: undefined,
    title: undefined,
  };

  return {
    ...defaultConfig,
    ...customConfig,
  };
}

export const getTaskId = async (data: object, createTaskUrl: string) => {
  if (!window.navigator.onLine) {
    return window.localStorage.getItem(LAST_SUCCESSFUL_TASK_ID);
  }

  return await axiosApi.post(createTaskUrl, data).then(({ data }) => data);
};

export function getFileDownloadingConfig(task: ITask, translate: boolean = false) {
  return getTaskConfig({
    createTaskURL: task.createTaskURL ?? getCreateTaskUrl(task),
    pingTaskURL: task.pingTaskURL ?? getPingTaskUrl(task),
    downloadURL: task.downloadURL ?? getDownloadTaskUrl(task),
    uniqId: task.itemId,
    statuses: translate
      ? [
          i18n.t('proposal_content.file.snackbar.statuses.preparing'),
          i18n.t('proposal_content.file.snackbar.statuses.generating'),
          i18n.t('proposal_content.file.snackbar.statuses.finishing'),
        ]
      : ['Preparing data...', 'Rendering content...', 'Finishing generating...'],
    filename: `${task.title}.${task.extension}`,
    title: task.title,
    translate,
    hint:
      task.extension === 'docx' ? (
        <p style={{ marginTop: '20px', maxWidth: '470px', fontSize: '18px' }}>
          Note: the cover page image displays correctly when the doc is saved as PDF or printed.
        </p>
      ) : null,
  } as ITaskConfig);
}

function getCreateTaskUrl(task: ITask) {
  return `files/${task.queue}`;
}

function getPingTaskUrl(task: ITask) {
  return `${task.queue}/${task.taskId}/status`;
}

function getDownloadTaskUrl(task: ITask) {
  return `${task.queue}/${task.taskId}/download`;
}
